"use client";

import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * Ảnh mascot. Tự tải từ /public/mascots/<name>.webp (đã tối ưu ≤256px cho nhẹ);
 * nếu chưa có file thì hiển thị khối màu tròn + emoji làm ảnh dự phòng.
 * Khi bổ sung mascot, chạy scripts/optimize-public-mascots.mjs để sinh .webp.
 */
export interface MascotProps {
  name: string;
  alt: string;
  /** Màu gradient dự phòng (Tailwind), vd "from-teal-300 to-teal-500". */
  fallback: string;
  emoji: string;
  className?: string;
}

export function Mascot({ name, alt, fallback, emoji, className }: MascotProps) {
  // "loading" khi SSR + lần render client đầu (khớp nhau, tránh hydration mismatch),
  // rồi useEffect kiểm tra ảnh đã tải được chưa (bắt cả trường hợp 404 trước hydrate).
  const [status, setStatus] = React.useState<"loading" | "ok" | "failed">("loading");
  const ref = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    const img = ref.current;
    if (!img) return;
    if (img.complete) {
      setStatus(img.naturalWidth > 0 ? "ok" : "failed");
    }
  }, []);

  if (status === "failed") {
    return (
      <div
        className={cn(
          "grid place-items-center bg-gradient-to-br shadow-sm ring-1 ring-black/5",
          fallback,
          className
        )}
        role="img"
        aria-label={alt}
      >
        <span className="text-[2.25rem] leading-none drop-shadow-sm sm:text-[2.75rem]">
          {emoji}
        </span>
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={`/mascots/${name}.webp`}
      alt={alt}
      onLoad={() => setStatus("ok")}
      onError={() => setStatus("failed")}
      className={cn("h-full w-full object-cover", status === "loading" && "opacity-0", className)}
    />
  );
}
